/**
 * Builds the static test world for Hurka — Issue #2.
 *
 * Layout (top-down view, +X right, +Z forward, origin = spawn):
 *
 *                 N
 *        ┌─────────────────┐
 *        │   ▲ tower       │
 *        │   │             │
 *        │ ┌─┴──┐ ┌────┐   │
 *        │ │    │ │house│  │      ← keep-out zone (walls + buildings)
 *        │ │keep│ └────┘   │
 *        │ │    │ ┌────┐   │
 *        │ └─┬──┘ │house│  │
 *        │   │    └────┘   │
 *        │   ▼ tower       │
 *        └─────────────────┘
 *
 * Everything is procedurally placed — no model files.  Issue #4 will
 * apply the procedural pixel textures from textures.ts to everything
 * here; this file just builds geometry + StandardMaterials.
 */

import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { Scene } from '@babylonjs/core/scene';

import {
  grassTexture,
  stoneFloorTexture,
  stoneWallTexture,
  woodPlankTexture,
  roofTileTexture,
} from './textures';
import { buildShowcaseHouse, type ShowcaseHouseHandles } from './houseMesh';
import { buildNature, type NatureHandles } from './nature';

/** Apply a StandardMaterial with the given diffuse texture (pixel-filtered). */
function textured(
  scene: Scene,
  name: string,
  diffuse: import('@babylonjs/core/Materials/Textures/texture').Texture
): StandardMaterial {
  const mat = new StandardMaterial(name, scene);
  mat.diffuseTexture = diffuse;
  // No PBR specular — Issue #4 requires no modern glossy effects.
  mat.specularColor = new Color3(0, 0, 0);
  return mat;
}

function placeWalls(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  width: number,
  depth: number,
  wallHeight: number,
  wallTex: import('@babylonjs/core/Materials/Textures/texture').Texture,
  roofTex: import('@babylonjs/core/Materials/Textures/texture').Texture
): void {
  // 4 walls as thin boxes
  const wallThickness = 0.3;
  const halfW = width / 2;
  const halfD = depth / 2;

  const walls = [
    { name: 'wallN', w: width, d: wallThickness, x: 0, z: halfD },
    { name: 'wallS', w: width, d: wallThickness, x: 0, z: -halfD },
    { name: 'wallE', w: wallThickness, d: depth, x: halfW, z: 0 },
    { name: 'wallW', w: wallThickness, d: depth, x: -halfW, z: 0 },
  ];
  for (const w of walls) {
    const m = MeshBuilder.CreateBox(w.name, { width: w.w, height: wallHeight, depth: w.d }, scene);
    m.position.set(w.x, wallHeight / 2, w.z);
    m.parent = parent;
    m.material = textured(scene, `${w.name}-mat`, wallTex);
    m.receiveShadows = true;
    shadowGen.addShadowCaster(m);
    // Collision: walls block the player.
    m.checkCollisions = true;
  }

  // Pitched roof — two slanted boxes meeting at a ridge along Z
  const roofHeight = 1.2;
  const roofY = wallHeight + roofHeight / 2;
  const slant = (w: number) =>
    MeshBuilder.CreateBox(`roof-${w}`, { width, height: roofHeight, depth: depth / 2 + 0.6 }, scene);
  // Babylon CSG-free pitched roof: rotate two boxes so they meet at the ridge.
  // Approximation: two boxes slanted inward by atan(roofHeight / (depth/2)).
  const angle = Math.atan2(roofHeight, depth / 2);
  const ridgeZ = 0;
  const roofMat = textured(scene, 'roof-mat', roofTex);
  for (const side of [-1, 1]) {
    const slab = MeshBuilder.CreateBox(
      side < 0 ? 'roofSouth' : 'roofNorth',
      { width, height: roofHeight * 2, depth: depth / 2 + 0.6 },
      scene
    );
    slab.position.set(0, wallHeight + roofHeight, ridgeZ + (side * depth) / 4);
    slab.rotation.x = side * angle;
    slab.parent = parent;
    slab.material = roofMat;
    slab.receiveShadows = true;
    shadowGen.addShadowCaster(slab);
    // Roofs are above the player's head, no collision needed (cheaper).
  }
}

interface BuildingOpts {
  name: string;
  x: number;
  z: number;
  width: number;
  depth: number;
  height: number;
  wallTex: import('@babylonjs/core/Materials/Textures/texture').Texture;
  roofTex: import('@babylonjs/core/Materials/Textures/texture').Texture;
  /** Rotation around Y in radians.  Default 0. */
  rotationY?: number;
}

function placeBuilding(scene: Scene, shadowGen: ShadowGenerator, parent: Mesh, opts: BuildingOpts): void {
  const b = MeshBuilder.CreateBox(opts.name, { width: 0.1, height: 0.1, depth: 0.1 }, scene);
  b.parent = parent;
  b.position.set(opts.x, 0, opts.z);
  b.rotation.y = opts.rotationY ?? 0;
  placeWalls(scene, shadowGen, b, opts.width, opts.depth, opts.height, opts.wallTex, opts.roofTex);
}

function placeWallSegment(
  scene: Scene,
  shadowGen: ShadowGenerator,
  parent: Mesh,
  name: string,
  length: number,
  x: number,
  z: number,
  rotationY: number,
  wallTex: import('@babylonjs/core/Materials/Textures/texture').Texture
): void {
  const wall = MeshBuilder.CreateBox(name, { width: length, height: 2, depth: 0.4 }, scene);
  wall.position.set(x, 1, z);
  wall.rotation.y = rotationY;
  wall.parent = parent;
  wall.material = textured(scene, `${name}-mat`, wallTex);
  wall.receiveShadows = true;
  shadowGen.addShadowCaster(wall);
  wall.checkCollisions = true;
}

/**
 * Aggregated handles returned by {@link buildScene}.  Holds the world root
 * (parent of everything outdoor) and the showcase-house handles for the
 * Issue #16 house (used by main.ts for door highlights + interior
 * show/hide).  `nature` exposes a per-frame update for the pond ripples.
 */
export interface SceneHandles {
  root: Mesh;
  showcase: ShowcaseHouseHandles;
  nature: NatureHandles;
}

/**
 * Build the static test world.
 *
 * @returns the {@link SceneHandles} bundle.  `root` is the world root
 *          TransformNode; `showcase` is the Issue #16 house (door, interior,
 *          furniture).  Dispose via `showcase.dispose()`.
 */
export function buildScene(scene: Scene, shadowGen: ShadowGenerator): SceneHandles {
  const root = new Mesh('world-root', scene);
  root.position.set(0, 0, 0);

  // ---- Ground: large grass plane ----
  const ground = MeshBuilder.CreateGround('ground', { width: 200, height: 200, subdivisions: 4 }, scene);
  ground.material = textured(scene, 'ground-mat', grassTexture(scene));
  ground.receiveShadows = true;
  ground.parent = root;
  // Ground catches the player so they don't fall through the world.
  ground.checkCollisions = true;

  // ---- Stone-paved courtyard in front of the keep ----
  const courtyard = MeshBuilder.CreateGround('courtyard', { width: 18, height: 18 }, scene);
  courtyard.position.set(0, 0.01, 0);
  courtyard.material = textured(scene, 'courtyard-mat', stoneFloorTexture(scene));
  courtyard.parent = root;
  courtyard.receiveShadows = true;
  // The courtyard is slightly above the grass; both need collision so the
  // player can walk on either.
  courtyard.checkCollisions = true;

  // ---- Shared wall + roof materials ----
  const wallTex = stoneWallTexture(scene);
  const roofTex = roofTileTexture(scene);
  const woodTex = woodPlankTexture(scene);

  // ---- Central keep (the main hall) ----
  placeBuilding(scene, shadowGen, root, {
    name: 'keep',
    x: 0,
    z: 6,
    width: 12,
    depth: 10,
    height: 5,
    wallTex,
    roofTex,
  });

  // ---- Two corner towers ----
  placeBuilding(scene, shadowGen, root, {
    name: 'towerNW',
    x: -8,
    z: 14,
    width: 4,
    depth: 4,
    height: 9,
    wallTex,
    roofTex,
  });
  placeBuilding(scene, shadowGen, root, {
    name: 'towerNE',
    x: 8,
    z: 14,
    width: 4,
    depth: 4,
    height: 9,
    wallTex,
    roofTex,
  });

  // ---- Issue #16 showcase house (replaces the static houseWest) ----
  const showcase = buildShowcaseHouse(scene, shadowGen);

  // NOTE: the placeholder `houseEast` (was at x=12, z=-6) was removed
  // because the world-fill nature pass drops the massive lake right over
  // that spot.  The showcase house alone is enough beach-front flavour
  // for now; more buildings can be added later if desired.

  // ---- Curtain wall sections (low perimeter walls) ----
  placeWallSegment(scene, shadowGen, root, 'curtainN', 24, 0, 22, 0, wallTex);
  placeWallSegment(scene, shadowGen, root, 'curtainS', 24, 0, -10, 0, wallTex);
  placeWallSegment(scene, shadowGen, root, 'curtainE', 16, 16, 6, Math.PI / 2, wallTex);
  placeWallSegment(scene, shadowGen, root, 'curtainW', 16, -16, 6, Math.PI / 2, wallTex);

  // ---- Wooden door on the keep's south face (decorative for now) ----
  const door = MeshBuilder.CreateBox('door', { width: 1.6, height: 2.4, depth: 0.1 }, scene);
  door.position.set(0, 1.2, 0.99);
  door.parent = root;
  door.material = textured(scene, 'door-mat', woodTex);
  shadowGen.addShadowCaster(door);
  // Door is thin — no collision needed (player walks through the doorway).

  // ---- Nature: trees, water, meadow, rocks, reeds ----
  // Placed outside the goblin patrol bounds (−12..12, −10..10) so the
  // enemy AI tick never runs into a tree trunk.  Animated water surface
  // needs the returned `update()` hook from main.ts' render loop.
  const nature = buildNature(scene, shadowGen, root);

  return { root, showcase, nature };
}

/**
 * Spawn position used by the FPS controller and the multiplayer sync layer.
 * Centered in the courtyard so the player can see the keep in front of them.
 */
export const SPAWN_POSITION = new Vector3(0, 1.7, -8);
